import axiosV2Instance from './axiosV2Instance';

/** Снимок брелков с r-station (обновляется при heartbeat). */
export interface StationRegisteredFob {
  keyfobUid: string;
  label?: string | null;
  /** Станция: брелок в эфире (реклама BLE в окне). */
  online?: boolean;
  lastBleSeenUtc?: string | null;
  /** Сервер: выключены автосмены по этому брелку (Keyfobs.IsBlocked). */
  isBlocked?: boolean;
}

/** Снимок anti-lost BLE (кэш heartbeat с r-station, Lenze/iTag watchlist). */
export interface StationAntilostTag {
  bleAddress: string;
  label?: string | null;
  profileCode?: number | null;
  lastSeenUtc?: string | null;
  online?: boolean | null;
  /** Сервер: выключены автосмены по этому идентификатору (Keyfobs.IsBlocked по MAC). */
  isBlocked?: boolean;
}

export interface Station {
  id: number;
  siteId: number;
  name: string;
  deviceUid: string;
  status: 'active' | 'blocked' | 'maintenance' | string;
  lastSeenAt?: string | null;
  firmwareVersion?: string | null;
  createdAt: string;
  /** true — сервер не запускает автоматику смен по событиям брелка с этой станции (лог событий не отключается). */
  blockFobShiftAutomation?: boolean;
  /** Минуты без сигнала брелка до автозакрытия смены; если не задано — глобальная настройка сервера (5). */
  fobShiftSilenceMinutes?: number | null;
  /** Минуты offline станции до автозакрытия после тишины брелка; если не задано — глобальная (30). */
  fobShiftOfflineGraceMinutes?: number | null;
  /** Минуты после автозакрытия для переоткрытия той же смены; если не задано — глобальная (10). */
  fobShiftReopenWindowMinutes?: number | null;
  registeredFobs?: StationRegisteredFob[];
  /** Anti-lost теги (BLE MAC), watchlist r-station. */
  antilostTags?: StationAntilostTag[];
  /** Публичный ID BLE-маяка (6 hex), напр. B23810. */
  beaconPublicId?: string | null;
  /** Станция только как маяк, без WiFi. */
  beaconOnly?: boolean;
}

export interface CreateStationRequest {
  name: string;
  apiKey?: string;
  beaconPublicId?: string;
}

export interface StationEvent {
  id: number;
  stationId: number;
  siteId: number;
  eventType: string;
  occurredAtDevice?: string | null;
  receivedAt: string;
  keyfobUid?: string | null;
  accessDecision?: string | null;
  reason?: string | null;
  correlationId?: string | null;
}

/** Активная привязка брелка (UID) к исполнителю на объекте. */
export interface KeyfobAssignment {
  id: number;
  keyfobId: number;
  keyfobUid: string;
  keyfobLabel?: string | null;
  keyfobIsBlocked: boolean;
  siteId: number;
  siteExecutorId: number;
  userId?: number | null;
  executorName: string;
  assignedAt: string;
  assignedBy?: number | null;
  unassignedAt?: string | null;
  unassignedBy?: number | null;
  reason?: string | null;
}

class StationsApi {
  async getStations(siteId: number): Promise<Station[]> {
    const response = await axiosV2Instance.get<Station[]>(`/sites/${siteId}/stations`);
    return response.data;
  }

  async getStationEvents(siteId: number, params?: { stationId?: number; limit?: number }): Promise<StationEvent[]> {
    const response = await axiosV2Instance.get<StationEvent[]>(`/sites/${siteId}/station-events`, { params });
    return response.data;
  }

  async rotateKey(siteId: number, stationId: number): Promise<{ apiKey: string }> {
    const response = await axiosV2Instance.post<{ apiKey: string }>(`/sites/${siteId}/stations/${stationId}/rotate-key`);
    return response.data;
  }

  async regenerateBeaconSecret(siteId: number, stationId: number): Promise<{ beaconSecret: string }> {
    const response = await axiosV2Instance.post<{ beaconSecret: string }>(
      `/sites/${siteId}/stations/${stationId}/regenerate-beacon-secret`
    );
    return response.data;
  }

  async updateStation(
    siteId: number,
    stationId: number,
    body: {
      name?: string;
      status?: 'active' | 'blocked' | 'maintenance';
      blockFobShiftAutomation?: boolean;
      fobShiftSilenceMinutes?: number;
      fobShiftOfflineGraceMinutes?: number;
      fobShiftReopenWindowMinutes?: number;
      beaconPublicId?: string;
    }
  ): Promise<{ beaconSecret?: string }> {
    // PUT: на проде nginx в CORS разрешает POST/PUT, но не PATCH (мобильный WebView/APK).
    const response = await axiosV2Instance.put<{ beaconSecret?: string }>(
      `/sites/${siteId}/stations/${stationId}`,
      body
    );
    return response.data ?? {};
  }

  async createStation(siteId: number, body: CreateStationRequest): Promise<{ id: number; beaconSecret?: string }> {
    const response = await axiosV2Instance.post<{ id: number; beaconSecret?: string }>(`/sites/${siteId}/stations`, {
      name: body.name.trim(),
      apiKey: body.apiKey?.trim() || undefined,
      beaconPublicId: body.beaconPublicId?.trim().toUpperCase() || undefined,
    });
    return response.data;
  }

  async getKeyfobAssignments(siteId: number, activeOnly = true): Promise<KeyfobAssignment[]> {
    const response = await axiosV2Instance.get<KeyfobAssignment[]>(`/sites/${siteId}/keyfob-assignments`, {
      params: { activeOnly },
    });
    return response.data;
  }

  async assignKeyfob(
    siteId: number,
    body: { keyfobUid: string; keyfobLabel?: string; siteExecutorId: number; reason?: string }
  ): Promise<{ id: number }> {
    const response = await axiosV2Instance.post<{ id: number }>(`/sites/${siteId}/keyfob-assignments`, {
      keyfobUid: body.keyfobUid.trim(),
      keyfobLabel: body.keyfobLabel?.trim() || undefined,
      siteExecutorId: body.siteExecutorId,
      reason: body.reason,
    });
    return response.data;
  }

  async unassignKeyfob(siteId: number, keyfobId: number, reason?: string): Promise<void> {
    await axiosV2Instance.post(`/sites/${siteId}/keyfob-assignments/unassign`, { keyfobId, reason });
  }

  async setKeyfobBlocked(siteId: number, keyfobUid: string, blocked: boolean): Promise<void> {
    // POST вместо PATCH — иначе CORS preflight блокируется nginx на ryaskov.pro (телефон/APK).
    await axiosV2Instance.post(`/sites/${siteId}/keyfobs/block`, {
      keyfobUid: keyfobUid.trim(),
      blocked,
    });
  }

  async deleteStation(siteId: number, stationId: number): Promise<void> {
    await axiosV2Instance.delete(`/sites/${siteId}/stations/${stationId}`);
  }
}

export const stationsApi = new StationsApi();
